import React from "react";
import { CheckIcon } from "../Icons";
import { CONTAINER } from "../ui";
import ProductShot, { Shot } from "./ProductShot";

// Same scale as ui.tsx's SectionHeading, which the landing and pricing pages use.
const HEADING =
  "tw-mt-4 tw-mb-0 tw-font-black tw-tracking-[-.045em] tw-text-5xl tw-leading-[1.04] sm:tw-text-[2.25rem] sm:tw-leading-[1.1]";

type MediaSide = "left" | "right";
type Tone = "white" | "tint" | "dark";

type Variant = "section" | "panel";

// A "panel" sits inside a section that already has its own heading, so it ranks below one: h3
// rather than h2, a smaller heading, and more of the row given to the screenshot.
const VARIANTS = {
  section: {
    tag: "h2",
    grid: "tw-grid-cols-2 tw-gap-14",
    heading: HEADING,
  },
  panel: {
    tag: "h3",
    grid: "tw-grid-cols-[1fr_1.7fr] tw-gap-16",
    heading:
      "tw-mt-3 tw-mb-0 tw-text-balance tw-font-black tw-tracking-[-.035em] tw-text-[2rem] tw-leading-[1.16]",
  },
} as const satisfies Record<Variant, { tag: "h2" | "h3"; grid: string; heading: string }>;

const TONE_SURFACE: Record<Tone, string> = {
  white: "tw-bg-white",
  tint: "tw-bg-[#f5f9fc]",
  dark: "tw-bg-[#10233f]",
};

// Secondary text on the navy surface is tinted from the same hue rather than greyed.
const TONE_TEXT: Record<Tone, { label: string; heading: string; body: string; bullet: string }> = {
  white: {
    label: "tw-text-[#0068b8]",
    heading: "tw-text-[#10233f]",
    body: "tw-text-[#536a82]",
    bullet: "tw-text-[#314b64]",
  },
  tint: {
    label: "tw-text-[#0068b8]",
    heading: "tw-text-[#10233f]",
    body: "tw-text-[#536a82]",
    bullet: "tw-text-[#314b64]",
  },
  dark: {
    label: "tw-text-[#70d9ce]",
    heading: "tw-text-white",
    body: "tw-text-[#c2d3e2]",
    bullet: "tw-text-[#dbe7f2]",
  },
};

export function PlatformSection({
  id,
  tone = "white",
  children,
}: {
  id?: string;
  tone?: Tone;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      aria-labelledby={id ? `${id}-heading` : undefined}
      className={`tw-scroll-mt-24 tw-py-16 sm:tw-py-12 ${TONE_SURFACE[tone]}`}
    >
      <div className={CONTAINER}>{children}</div>
    </section>
  );
}

// Heading on the left, supporting copy on the right — the header shape the reference uses above
// its capability grid and its dark engagement section.
export function ShowcaseHeader({
  id,
  label,
  heading,
  body,
  tone = "white",
}: {
  id?: string;
  label: string;
  heading: string;
  body?: string;
  tone?: Tone;
}) {
  const text = TONE_TEXT[tone];

  return (
    <div className="tw-grid tw-grid-cols-[.4fr_.6fr] tw-items-end tw-gap-8 sm:tw-grid-cols-1 sm:tw-gap-5">
      <div>
        <p className={`tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] ${text.label}`}>
          {label}
        </p>
        <h2
          id={id ? `${id}-heading` : undefined}
          className={`${HEADING} ${text.heading}`}
        >
          {heading}
        </h2>
      </div>

      {body ? <p className={`tw-m-0 tw-text-lg tw-leading-8 ${text.body}`}>{body}</p> : null}
    </div>
  );
}

// The alternating copy/screenshot block. `mediaSide` is a union rather than a `reverse` flag so a
// third arrangement would not need a second boolean.
export function PlatformShowcase({
  headingId,
  label,
  heading,
  body,
  bullets,
  shot,
  mediaSide = "right",
  mediaAspect,
  variant = "section",
  tone = "white",
  priority = false,
  children,
}: {
  headingId?: string;
  label: string;
  heading: string;
  body: string;
  bullets?: readonly string[];
  shot: Shot;
  mediaSide?: MediaSide;
  mediaAspect?: number;
  variant?: Variant;
  tone?: Tone;
  priority?: boolean;
  children?: React.ReactNode;
}) {
  const text = TONE_TEXT[tone];
  const { tag: Heading, grid, heading: headingClass } = VARIANTS[variant];

  return (
    <div className={`tw-grid tw-items-center ${grid} sm:tw-grid-cols-1 sm:tw-gap-10`}>
      <div className="tw-min-w-0">
        <p className={`tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] ${text.label}`}>
          {label}
        </p>
        <Heading id={headingId} className={`${headingClass} ${text.heading}`}>
          {heading}
        </Heading>
        <p className={`tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 ${text.body}`}>{body}</p>

        {bullets ? (
          <ul className="tw-mt-7 tw-mb-0 tw-list-none tw-space-y-3.5 tw-p-0">
            {bullets.map((bullet) => (
              <li
                key={bullet}
                className={`tw-flex tw-gap-3 tw-text-base tw-font-bold tw-leading-7 ${text.bullet}`}
              >
                <CheckIcon
                  aria-hidden
                  className={`tw-mt-1 tw-h-4 tw-w-4 tw-shrink-0 ${
                    tone === "dark" ? "tw-text-[#70d9ce]" : "tw-text-[#0068b8]"
                  }`}
                />
                {bullet}
              </li>
            ))}
          </ul>
        ) : null}

        {children}
      </div>

      <div
        className={`tw-min-w-0 ${
          mediaSide === "left" ? "tw-order-first sm:tw-order-none" : "tw-order-last sm:tw-order-none"
        }`}
      >
        <ProductShot shot={shot} priority={priority} aspect={mediaAspect} />
      </div>
    </div>
  );
}

// A screenshot with its own caption beneath, for the two side-by-side cards on the navy section.
export function MediaCard({
  shot,
  label,
  heading,
  body,
  tone = "dark",
}: {
  shot: Shot;
  label: string;
  heading: string;
  body: string;
  tone?: Tone;
}) {
  const text = TONE_TEXT[tone];

  return (
    <div className="tw-min-w-0">
      <ProductShot shot={shot} />

      <p className={`tw-mt-7 tw-mb-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.14em] ${text.label}`}>
        {label}
      </p>
      <h3 className={`tw-mt-3 tw-mb-0 tw-text-2xl tw-font-black tw-leading-8 tw-tracking-[-.03em] ${text.heading}`}>
        {heading}
      </h3>
      <p className={`tw-mt-3 tw-mb-0 tw-text-base tw-leading-7 ${text.body}`}>{body}</p>
    </div>
  );
}
